import { ArrowDropDown } from "@mui/icons-material";
import { Button, CircularProgress, Menu, MenuItem } from "@mui/material";
import { red } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { get, includes } from "lodash";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../../../common/js/components/ConfirmationDialog";
import SaveAndExitButton from "../../../common/js/components/SaveAndExitButton";
import { Comments } from "../../../common/js/icons";
import constants from "./constants";

const StyledPageToolbar = styled("div")({
    padding: "10px 10px",
    backgroundColor: "#fafafa",
    position: "sticky",
    top: "62px",
    margin: "0 0 20px 0",
    width: "100%",
    zIndex: 2,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
});

const StyledPageToolbarButtonContainer = styled("div")({
    display: "flex",
    alignItems: "center",
});

const StyledProgressDiv = styled("div")({
    textAlign: "center",
    width: "5em",
    display: "inline-block",
});

const StyledDeleteButton = styled(Button)({
    backgroundColor: red[500],
    color: "white",
    "&:hover": {
        backgroundColor: red[800],
    },
});

const StyledInactiveButton = styled(Button)({
    marginLeft: 0,
});

const StyledMenuSubItem = styled(MenuItem)({
    "&>a": {
        paddingLeft: "30px !important",
    },
});

const StyledCommentsButton = styled(Button)({
    color: "black",
});

const ListingToolbar = (props) => {
    const {
        brokerSubmitListingRequest,
        confirmDialog,
        convertListingType,
        copyPropertyPhotoToListing,
        deleteListingDialog,
        duplicateListingDialog,
        duplicateSuccessDialog,
        disableInputs,
        documentTypes,
        elementsSaving,
        handleDeleteListing,
        handleDisplayDeleteDialog,
        handleDisplayInactivateDialog,
        handleDuplicateListing,
        handleDuplicateSuccessDialog,
        handleInactivateListing,
        handleListingDuplicateDialog,
        handleSubmit,
        handleSave,
        handleSaveAndExit,
        inactivateListingDialog,
        listing,
        numComments,
        toggleComments,
        toggleConfirm,
        userProfile,
        checkIfListingIsAlreadyAssigned,
        propDocumentTypes,
    } = props;
    const navigate = useNavigate();
    const [mainActionAnchorEl, setMainActionAnchorEl] = useState(null);
    const mainActionDropdown = Boolean(mainActionAnchorEl);
    const [convertDialogOpen, toggleConvertDialogOpen] = useState(false);
    const allowSwitchType = ["Draft", "Broker Action Required"].includes(listing?.status);

    const handleCancel = () => {
        toggleConfirm(null);
    };

    const handleMainActionDropdown = (event) => {
        setMainActionAnchorEl(event.currentTarget);
    };

    const handleCloseMainActionDropdown = () => {
        setMainActionAnchorEl(null);
    };

    const handleStart = async () => {
        const result = await checkIfListingIsAlreadyAssigned(listing.listingKey);
        if (result && !result.success) {
            navigate("/listings");
        } else {
            handleSubmit(false, constants.urls.assign_listing_for_review(listing.listingKey), true);
        }
    };

    const submitActions = {
        broker: {
            Draft: {
                buttonText: "Submit",
                submit: () => {
                    handleBrokerSubmit(false);
                },
                variant: "contained",
                confirm: true,
            },
            "Broker Action Required": {
                buttonText: "Submit",
                submit: () => {
                    handleSubmitAndRedirect(true, constants.urls.broker_submit(listing.listingKey), true);
                },
                variant: "contained",
                confirm: false,
                url: constants.urls.broker_submit,
            },
        },
        dataEntry: {
            "Needs Review": {
                buttonText: "Start",
                submit: () => {
                    handleStart();
                },
                confirm: false,
                url: constants.urls.assign_listing_for_review,
            },
            "Update Requested": {
                buttonText: "Update",
                submit: (callback) => {
                    handleSubmitAndRedirect(true, callback);
                },
                confirm: true,
                options: {
                    "Update Action Required": constants.urls.reassign_active_listing_to_broker,
                    "Update & Approve": constants.urls.approve_listing,
                    "Update & Set As Pending": constants.urls.pending_listing,
                },
            },
            "In Review": {
                buttonText: "In Review",
                submit: (callback) => {
                    handleSubmitAndRedirect(true, callback);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_listing_to_broker,
                    Approve: constants.urls.approve_listing,
                },
            },
        },
        dataAdmin: {
            "Needs Review": {
                buttonText: "Start",
                submit: () => {
                    handleStart();
                },
                confirm: false,
                url: constants.urls.assign_listing_for_review,
            },
            "Update Requested": {
                buttonText: "Update",
                submit: (callback) => {
                    handleSubmitAndRedirect(true, callback);
                },
                confirm: true,
                options: {
                    "Update Action Required": constants.urls.reassign_active_listing_to_broker,
                    "Update & Approve": constants.urls.approve_listing,
                    "Update & Set As Pending": constants.urls.pending_listing,
                },
            },
            "In Review": {
                buttonText: "In Review",
                submit: (callback) => {
                    handleSubmitAndRedirect(true, callback);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_listing_to_broker,
                    Approve: constants.urls.approve_listing,
                },
            },
        },
    };

    const actionRole = includes(["dataEntry", "dataAdmin"], userProfile.role) ? userProfile.role : "broker";
    const mainAction = get(submitActions, `${actionRole}.${listing.status}`, null);

    const handleSaveAndRedirect = async () => {
        const result = await handleSaveAndExit();
        redirectToListings(result.success);
    };

    const handleBrokerSubmit = async () => {
        const result = await brokerSubmitListingRequest(false);
        redirectToListings(result.success);
    };

    const handleSubmitAndRedirect = async (save, callback, confirmIsOpen, key) => {
        const result = await handleSubmit(save, callback, confirmIsOpen, key);
        redirectToListings(result.success);
    };

    const handleInactivateAndRedirect = async () => {
        const result = await handleInactivateListing(listing.listingKey);
        redirectToListings(result.success);
    };

    const duplicateListingAndRedirect = async () => {
        const result = await handleDuplicateListing(listing.listingKey);
        if (result) {
            if (result.listingKey) {
                navigate("/listing/" + result.listingKey + "?action=duplicate");
                window.location.reload();
            }
        }
    };
    const handleDeleteAndRedirect = async () => {
        const result = await handleDeleteListing(listing.listingKey);
        redirectToListings(result.success);
    };

    const redirectToListings = (allowRedirect) => {
        allowRedirect && navigate("/listings");
    };

    const confirm = (url) => {
        toggleConfirm(url(listing.listingKey));
    };

    const handleCopyPropertyPhotoToListing = async () => {
        const result = await copyPropertyPhotoToListing(listing.listingKey);
        redirectToListings(result.success);
    };

    const hasNoPrimaryPhoto = !Object.values(documentTypes).some(
        (document) => document?.documentType?.name === "Primary Photo"
    );

    const brokerDraftMissingPriPhoto = hasNoPrimaryPhoto && actionRole === "broker" && listing.status === "Draft";

    const propertyDocumentTypes = Object.values(propDocumentTypes).map((document) => document?.documentType?.name);
    const propertyPhotoExist = includes(propertyDocumentTypes, "Primary Photo");

    const canInactivate =
        includes(["Active", "Update Requested", "Pending"], get(listing, "status", false)) &&
        (get(listing, "nonKmListingFlg") ? true : includes(["dataAdmin", "dataEntry"], userProfile.role));

    const kmCanDelete =
        !includes(["Active"], get(listing, "status", null)) &&
        (includes(["dataAdmin", "dataEntry"], userProfile.role) ||
            userProfile.user === get(listing, "auditCreatedBy", null));

    const nonKmCanDelete =
        get(listing, "nonKmListingFlg", false) &&
        includes(["appraiser", "dataAdmin", "dataEntry", "research", "researchAdmin"], userProfile.role);

    return (
        <React.Fragment>
            <StyledPageToolbar>
                <div>
                    <h2 className="page-title">Listing Request Form</h2>
                    <p className="page-subtitle">
                        {get(listing, "attributes.useType")} {get(listing, "listingType")}
                    </p>
                </div>
                <div>
                    <StyledPageToolbarButtonContainer>
                        {canInactivate && (
                            <div>
                                {elementsSaving.listingInactivate ? (
                                    <StyledProgressDiv>
                                        <CircularProgress size={20} color="secondary" />
                                    </StyledProgressDiv>
                                ) : (
                                    <StyledInactiveButton
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => handleDisplayInactivateDialog(true)}
                                    >
                                        Inactivate
                                    </StyledInactiveButton>
                                )}
                            </div>
                        )}
                        {allowSwitchType && (
                            <div>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => toggleConvertDialogOpen(true)}
                                >
                                    Convert to {listing?.listingType === "Lease" ? "Sale" : "Lease"}
                                </Button>
                            </div>
                        )}
                        <div>
                            {elementsSaving.listingDuplicate ? (
                                <StyledProgressDiv>
                                    <CircularProgress size={20} color="primary" />
                                </StyledProgressDiv>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleListingDuplicateDialog(true)}
                                >
                                    Duplicate
                                </Button>
                            )}
                        </div>
                        {(kmCanDelete || nonKmCanDelete) && (
                            <div>
                                {elementsSaving.listingDelete ? (
                                    <StyledProgressDiv>
                                        <CircularProgress size={20} color="secondary" />
                                    </StyledProgressDiv>
                                ) : (
                                    <StyledDeleteButton
                                        variant="contained"
                                        onClick={() => handleDisplayDeleteDialog(true)}
                                    >
                                        Delete Listing
                                    </StyledDeleteButton>
                                )}
                            </div>
                        )}
                        <div>
                            <StyledCommentsButton onClick={toggleComments} data-testid="commentsButton">
                                <Comments />
                                &nbsp;Comments ({numComments})
                            </StyledCommentsButton>
                        </div>
                        <div>
                            {elementsSaving.listingSaveAndExit ? (
                                <StyledProgressDiv>
                                    <CircularProgress size={20} color="primary" />
                                </StyledProgressDiv>
                            ) : (
                                <React.Fragment>
                                    <SaveAndExitButton
                                        onSave={handleSave}
                                        onSaveAndExit={handleSaveAndRedirect}
                                        disabled={disableInputs}
                                    />
                                </React.Fragment>
                            )}
                        </div>
                        <div>
                            {elementsSaving.listingSubmit ? (
                                <StyledProgressDiv>
                                    <CircularProgress size={20} color="primary" />
                                </StyledProgressDiv>
                            ) : mainAction && mainAction.options ? (
                                <React.Fragment>
                                    <Button
                                        id="Main-Action-Menu"
                                        aria-controls={mainActionDropdown ? "Main-Action-Menu" : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={mainActionDropdown ? "true" : undefined}
                                        onClick={handleMainActionDropdown}
                                        variant="contained"
                                        endIcon={<ArrowDropDown />}
                                    >
                                        {get(mainAction, "buttonText")}
                                    </Button>
                                    <Menu
                                        id="Main-Action-Menu"
                                        anchorEl={mainActionAnchorEl}
                                        open={mainActionDropdown}
                                        onClose={handleCloseMainActionDropdown}
                                        anchorOrigin={{
                                            vertical: "bottom", // Align the top of the menu with the bottom of the button
                                            horizontal: "right", // Align the right edge of the menu with the right edge of the button
                                        }}
                                        transformOrigin={{
                                            vertical: "top", // Align the bottom of the menu with the top of the button
                                            horizontal: "right", // Align the left edge of the menu with the right edge of the button
                                        }}
                                    >
                                        {Object.keys(get(mainAction, "options", {})).map((x) => {
                                            let url = get(mainAction, `options.${x}`);
                                            return (
                                                <StyledMenuSubItem
                                                    style={{ fontSize: "14px" }}
                                                    onClick={() => confirm(url)}
                                                >
                                                    {x}
                                                </StyledMenuSubItem>
                                            );
                                        })}
                                    </Menu>
                                </React.Fragment>
                            ) : mainAction !== null ? (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => (mainAction.confirm ? toggleConfirm() : mainAction["submit"]())}
                                >
                                    {mainAction.buttonText}
                                </Button>
                            ) : (
                                listing && (
                                    <Button disabled variant="contained" color="secondary">
                                        {listing.status}
                                    </Button>
                                )
                            )}
                        </div>
                    </StyledPageToolbarButtonContainer>
                </div>
            </StyledPageToolbar>
            <ConfirmationDialog
                open={confirmDialog.open && !brokerDraftMissingPriPhoto}
                dialogTitle={"Continue?"}
                dialogContent={"Are you sure you want to submit this status change?"}
                onCancel={handleCancel}
                onOk={() => mainAction["submit"](confirmDialog.onSubmit)}
            />
            <ConfirmationDialog
                open={confirmDialog.open && brokerDraftMissingPriPhoto && propertyPhotoExist}
                dialogTitle={"Missing Primary Photo?"}
                dialogContent={
                    'Since you did not attach a Primary Photo, the Property Photo will be used by default. Click "OK" to\n continue or "Back" to add a Primary Photo.'
                }
                onCancel={handleCancel}
                onOk={handleCopyPropertyPhotoToListing}
            />
            <ConfirmationDialog
                open={confirmDialog.open && brokerDraftMissingPriPhoto && !propertyPhotoExist}
                dialogTitle={"Continue?"}
                dialogContent={
                    'You did not attach a Primary Photo, and the Property associated with this Listing does not have a\n photo. Click "OK" to have the Corporate Data Team add your Primary Photo or "Back" to add a Primary\n Photo.'
                }
                onCancel={handleCancel}
                onOk={() => mainAction["submit"](confirmDialog.onSubmit)}
            />
            <ConfirmationDialog
                open={inactivateListingDialog.open}
                dialogTitle={"Inactivate Listing?"}
                dialogContent={"Are you sure you want to permanently inactivate this listing?"}
                onCancel={() => handleDisplayInactivateDialog(false)}
                onOk={handleInactivateAndRedirect}
            />
            <ConfirmationDialog
                dialogTitle={"Delete Listing?"}
                dialogContent={
                    "Are you sure you want to permanently delete this listing? The listing cannot be recovered once it has been deleted."
                }
                open={deleteListingDialog.open}
                onCancel={() => handleDisplayDeleteDialog(false)}
                onOk={handleDeleteAndRedirect}
            />
            <ConfirmationDialog
                dialogTitle={"Duplicate Listing?"}
                dialogContent={
                    "Are you sure you want to create a new Listing Request with the same information as this Listing?"
                }
                onOk={duplicateListingAndRedirect}
                onCancel={() => handleListingDuplicateDialog(false)}
                open={duplicateListingDialog.open}
            />
            <ConfirmationDialog
                open={duplicateSuccessDialog.open}
                dialogContent={"You are now viewing your new duplicated Listing."}
                onOk={() => handleDuplicateSuccessDialog(false)}
                hideCancel
            />
            <ConfirmationDialog
                open={convertDialogOpen}
                dialogTitle={"Convert Listing Type"}
                dialogContent={`Are you sure you want to convert this listing to a
                    ${listing?.listingType === "Sale" ? "Lease" : "Sale"}`}
                onOk={() => {
                    toggleConvertDialogOpen(false);
                    convertListingType();
                }}
                onCancel={() => toggleConvertDialogOpen(false)}
            />
        </React.Fragment>
    );
};

export default ListingToolbar;
